import { Mesh } from 'three/src/objects/Mesh';
import { StopeSourceBounds } from 'modules/project/StopeFillPage/Three/useStopeModel';
import { BeachSurface } from 'modules/project/StopeFillPage/Three/pour';

export interface PreviewPour {
  volume: number;
  height?: number;
  // The form's per-pour beach angle override, if the user typed one. Undefined means "use the project
  // default" - the preview resolves it through the same chain the created pour will use.
  beachAngleDegrees?: number;
}

export interface PourVolumeInfo {
  entityId: string;
  name: string;
  /** The volume STORED on the pour. */
  volume: number;
  /** The volume the current stope shape says this slice actually holds (from the exact integral). */
  recomputedVolume?: number;
  /** The beach slope this pour was planned at: its own stored angle, or 1.25 if it predates the field. */
  beachAngleDegrees?: number;
  height?: number;
}

export interface StopeDimensions {
  length: number;
  width: number;
  height: number;
}

export interface VolumeData {
  totalVolume: number;
  remainingVolume: number;
  pours: PourVolumeInfo[];
  dimensions?: StopeDimensions;
}

export type InputMode = 'height' | 'volume';

export interface StopeFillSceneState {
  pipePosition: { x: number; y: number } | null;
  selectedPourId: string | undefined;
  pourColorDisplay: 'by-pour' | 'by-recipe';
  enablePositionMove: boolean;
  previewPour: PreviewPour | null;
  volumeData: VolumeData | null;
  version: number;
  remainingStopeModel: Mesh | null;
  originalStopeModel: Mesh | null;
  // Source-file bbox figures for mapping local-frame geometry back to the mine grid (see
  // StopeSourceBounds); null until the stope geometry has loaded.
  stopeSourceBounds: StopeSourceBounds | null;
  // Every beach placed by the walk that drew the committed pours, in order. The fill surface is their
  // pointwise maximum, and the preview and the height/volume form fields are all derived from it - so
  // what the form shows describes the same solid the pour will actually be created as.
  fillSurfaces: BeachSurface[];
  // Volume already poured. From the exact integral over those surfaces, never from CSG.
  cumulativeVolume: number;
  // The floor of the solid under the FIRST pour's pipe, probed once against the pristine mesh.
  dischargeFloorLevel: number;
  inputMode: InputMode;
  currentVolume: number | null;
  readyGeometryPath: string | null;
}

let sceneState: StopeFillSceneState = {
  pipePosition: null,
  selectedPourId: undefined,
  pourColorDisplay: 'by-pour',
  enablePositionMove: true,
  previewPour: null,
  volumeData: null,
  version: 0,
  remainingStopeModel: null,
  originalStopeModel: null,
  stopeSourceBounds: null,
  fillSurfaces: [],
  cumulativeVolume: 0,
  dischargeFloorLevel: 0,
  inputMode: 'height',
  currentVolume: null,
  readyGeometryPath: null,
};

const stateListeners: Set<() => void> = new Set();

export const getStopeFillSceneState = () => sceneState;

export const updateStopeFillSceneState = (updates: Partial<StopeFillSceneState>) => {
  sceneState = { ...sceneState, ...updates, version: sceneState.version + 1 };
  stateListeners.forEach((listener) => listener());
};

export const subscribeToStopeFillSceneState = (listener: () => void): (() => void) => {
  stateListeners.add(listener);
  return () => { stateListeners.delete(listener); };
};

export const resetStopeFillSceneState = () => {
  sceneState = {
    pipePosition: null,
    selectedPourId: undefined,
    pourColorDisplay: 'by-pour',
    enablePositionMove: true,
    previewPour: null,
    volumeData: null,
    version: 0,
    remainingStopeModel: null,
    originalStopeModel: null,
    stopeSourceBounds: null,
    fillSurfaces: [],
    cumulativeVolume: 0,
    dischargeFloorLevel: 0,
    inputMode: 'height',
    currentVolume: null,
    readyGeometryPath: null,
  };
  stateListeners.forEach((listener) => listener());
};
